import { Link } from 'react-router-dom';
import { mainSiteUrl } from '@/mocks/homeData';

export default function ContentSections() {
  return (
    <>
      {/* What is BaseTao Spreadsheet */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-10 items-center">
            <div className="flex-1">
              <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-4">
                Getting Started
              </span>
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-5">
                What Is BaseTao Spreadsheet?
              </h2>
              <div className="space-y-4 text-foreground-600 text-sm md:text-base leading-relaxed">
                <p>
                  <strong className="text-foreground-900">BaseTao Spreadsheet</strong> is a powerful organizational 
                  tool designed for shoppers who want to track and manage their purchases through the BaseTao 
                  platform. Rather than juggling random notes and scattered bookmarks, a well-structured 
                  BaseTao Spreadsheet brings everything into one clear, actionable dashboard.
                </p>
                <p>
                  The concept behind the BaseTao Spreadsheet is refreshingly straightforward: create columns for 
                  <strong className="text-foreground-900"> product names, prices, shipping estimates, warehouse status, 
                  and final landed costs</strong>. This approach transforms chaotic shopping sessions into a structured 
                  workflow where every item&apos;s journey from purchase to delivery is visible at a glance.
                </p>
                <p>
                  What makes the BaseTao Spreadsheet approach particularly effective is how it adapts to different 
                  shopping styles. Some users prefer detailed cost-tracking with automated calculations, while others 
                  keep things minimal with just the essential fields. The flexibility is entirely in your hands.
                </p>
              </div>
              <Link
                to="/basetao-spreadsheet/"
                className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-primary-500 text-background-50 rounded-xl text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
              >
                Read the Full Spreadsheet Guide
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </Link>
            </div>
            <div className="flex-shrink-0 w-full lg:w-[380px]">
              <div className="rounded-2xl overflow-hidden border border-background-200/70">
                <img
                  src="https://readdy.ai/api/search-image?query=Clean%20modern%20laptop%20displaying%20organized%20spreadsheet%20with%20product%20data%20columns%20and%20tracking%20status%2C%20warm%20cream%20workspace%20with%20natural%20light%20from%20window%2C%20minimal%20desk%20setup%20with%20small%20plant%2C%20professional%20editorial%20photography%2C%20calm%20organized%20atmosphere%20with%20neutral%20tones&width=800&height=600&seq=content-sp-2026&orientation=landscape"
                  alt="BaseTao Spreadsheet interface example"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Use a Spreadsheet */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
              Benefits
            </span>
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-3">
              Why Smart Shoppers Use BaseTao Spreadsheet
            </h2>
            <p className="text-foreground-500 text-sm max-w-xl mx-auto">
              Here is why a structured spreadsheet approach makes a measurable difference in your BaseTao shopping experience.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="p-6 rounded-xl bg-background-50 border border-background-200/70">
              <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center mb-4">
                <i className="ri-funds-line text-lg text-primary-600"></i>
              </div>
              <h3 className="font-heading text-base text-foreground-900 mb-2">Cost Clarity</h3>
              <p className="text-sm text-foreground-500 leading-relaxed">
                Track every dollar from product price through shipping fees to final landed cost. 
                No more surprises when your haul arrives. The BaseTao Spreadsheet approach gives you 
                complete financial visibility across your entire shopping list.
              </p>
            </div>
            <div className="p-6 rounded-xl bg-background-50 border border-background-200/70">
              <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center mb-4">
                <i className="ri-truck-line text-lg text-accent-600"></i>
              </div>
              <h3 className="font-heading text-base text-foreground-900 mb-2">Shipping Optimization</h3>
              <p className="text-sm text-foreground-500 leading-relaxed">
                Compare shipping methods side by side and calculate the most cost-effective way 
                to ship your items. Group compatible products together and identify opportunities 
                to consolidate shipments for better rates.
              </p>
            </div>
            <div className="p-6 rounded-xl bg-background-50 border border-background-200/70">
              <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center mb-4">
                <i className="ri-store-2-line text-lg text-secondary-600"></i>
              </div>
              <h3 className="font-heading text-base text-foreground-900 mb-2">Warehouse Tracking</h3>
              <p className="text-sm text-foreground-500 leading-relaxed">
                Monitor every item&apos;s warehouse status from arrival through inspection to storage. 
                Know exactly which products are ready to ship and which are still pending, 
                so you never lose track of a single item.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-4">
              Comparison
            </span>
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-3">
              BaseTao Spreadsheet vs Traditional Spreadsheets
            </h2>
            <p className="text-foreground-500 text-sm max-w-xl mx-auto">
              See how a purpose-built BaseTao Spreadsheet approach compares to generic alternatives.
            </p>
          </div>

          <div className="overflow-hidden rounded-xl border border-background-200/70">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-background-100">
                  <th className="text-left px-6 py-4 font-heading text-foreground-900">Feature</th>
                  <th className="text-left px-6 py-4 font-heading text-primary-700">BaseTao Spreadsheet</th>
                  <th className="text-left px-6 py-4 font-heading text-foreground-600">Traditional Spreadsheet</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200/60">
                {[
                  { feature: 'Templates', base: 'Pre-built & Ready', trad: 'Build from scratch' },
                  { feature: 'Cost Tracking', base: 'Advanced & Automated', trad: 'Basic manual entry' },
                  { feature: 'Shipping Calculator', base: 'Built-in Estimation', trad: 'Requires manual formulas' },
                  { feature: 'Organization', base: 'Structured & High', trad: 'Flexible but unstructured' },
                  { feature: 'Warehouse Sync', base: 'Trackable Status Fields', trad: 'Not applicable' },
                ].map((row, i) => (
                  <tr key={i}>
                    <td className="px-6 py-4 text-foreground-700 font-medium">{row.feature}</td>
                    <td className="px-6 py-4 text-foreground-600">
                      <span className="inline-flex items-center gap-1.5 text-primary-600">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-check-line text-xs"></i>
                        </div>
                        {row.base}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-foreground-500">{row.trad}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="relative py-20 md:py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20product%20display%20background%20with%20soft%20sage%20green%20and%20cream%20fabric%20textures%2C%20subtle%20layered%20geometric%20patterns%2C%20clean%20modern%20aesthetic%20for%20ecommerce%2C%20gentle%20gradient%20lighting%2C%20calm%20professional%20atmosphere%2C%20abstract%20textile%20and%20paper%20texture%20blend&width=1920&height=600&seq=cta-product-2026&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-background-950/60"></div>

        <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-heading text-background-50 mb-4">
            Ready to Organize Your BaseTao Shopping?
          </h2>
          <p className="text-background-200/80 text-sm md:text-base mb-8 max-w-xl mx-auto">
            Start using BaseTao Spreadsheet to track products, compare costs, and optimize your 
            shipping. Everything you need in one structured, easy-to-use system.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-8 py-3.5 bg-primary-500 text-background-50 rounded-xl text-base font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              BaseTao Spreadsheet
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-8 py-3.5 bg-accent-500 text-background-50 rounded-xl text-base font-semibold hover:bg-accent-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
            <Link
              to="/basetao-spreadsheet/"
              className="px-8 py-3.5 bg-background-50/10 backdrop-blur-sm text-background-50 border border-background-50/20 rounded-xl text-base font-semibold hover:bg-background-50/20 transition-all duration-200 whitespace-nowrap"
            >
              Read SEO Guide
            </Link>
          </div>
        </div>
      </section>

      {/* Spreadsheet Showcase */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
              Interactive Demo
            </span>
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-3">
              Spreadsheet Dashboard Preview
            </h2>
            <p className="text-foreground-500 text-sm max-w-xl mx-auto">
              Below is a sample of what an organized BaseTao Spreadsheet looks like. 
              Track products, prices, shipping estimates, warehouse status, and final costs all in one place.
            </p>
          </div>

          <div className="overflow-hidden rounded-xl border border-background-200/70">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-background-100">
                  <th className="text-left px-5 py-3.5 font-heading text-foreground-900 text-xs">Product</th>
                  <th className="text-left px-5 py-3.5 font-heading text-foreground-900 text-xs">Price</th>
                  <th className="text-left px-5 py-3.5 font-heading text-foreground-900 text-xs">Shipping</th>
                  <th className="text-left px-5 py-3.5 font-heading text-foreground-900 text-xs">Warehouse</th>
                  <th className="text-left px-5 py-3.5 font-heading text-foreground-900 text-xs">Status</th>
                  <th className="text-left px-5 py-3.5 font-heading text-foreground-900 text-xs">Final Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200/60">
                {[
                  { product: 'Nike Air Max 270', price: '$42.00', shipping: '$18.50', warehouse: 'Stored', status: 'Ready', cost: '$60.50' },
                  { product: 'Supreme Box Logo Hoodie', price: '$28.00', shipping: '$22.00', warehouse: 'Stored', status: 'Ready', cost: '$50.00' },
                  { product: 'Yeezy Slide Bone', price: '$15.00', shipping: '$12.00', warehouse: 'Pending', status: 'Processing', cost: '$27.00' },
                  { product: 'Stone Island Cargo Pants', price: '$35.00', shipping: '$19.00', warehouse: 'Stored', status: 'Ready', cost: '$54.00' },
                  { product: 'New Era 59FIFTY Cap', price: '$8.00', shipping: '$9.50', warehouse: 'Pending', status: 'Awaiting QC', cost: '$17.50' },
                ].map((row, i) => (
                  <tr key={i} className="hover:bg-background-100/50 transition-colors">
                    <td className="px-5 py-3.5 text-foreground-700 font-medium">{row.product}</td>
                    <td className="px-5 py-3.5 text-foreground-600">{row.price}</td>
                    <td className="px-5 py-3.5 text-foreground-600">{row.shipping}</td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        row.warehouse === 'Stored' 
                          ? 'bg-primary-100 text-primary-700' 
                          : 'bg-accent-100 text-accent-700'
                      }`}>
                        {row.warehouse}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        row.status === 'Ready' 
                          ? 'bg-primary-100 text-primary-700' 
                          : 'bg-secondary-100 text-secondary-700'
                      }`}>
                        {row.status}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-foreground-700 font-semibold">{row.cost}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-center text-sm text-foreground-400 mt-6">
            The BaseTao Spreadsheet helps users organize shopping research, compare costs, 
            and track orders efficiently. This structured approach eliminates guesswork and 
            provides complete visibility into your entire shopping workflow.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-4">
              FAQ
            </span>
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-3">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-4">
            {[
              {
                q: 'What is a BaseTao Spreadsheet?',
                a: 'A BaseTao Spreadsheet is a structured organizational tool that helps shoppers track products, prices, shipping costs, warehouse statuses, and final expenses when using the BaseTao platform. It replaces scattered notes and bookmarks with one clear, searchable record of every item and its associated costs.',
              },
              {
                q: 'Is BaseTao Spreadsheet free to use?',
                a: 'Yes, the BaseTao Spreadsheet concept is free. You can create your own using any spreadsheet software. The organizational methodology and templates we share on this website are provided at no cost as educational resources.',
              },
              {
                q: 'How do I track shipping costs with a BaseTao Spreadsheet?',
                a: 'Create dedicated columns for estimated shipping weight, shipping method, estimated cost, and actual cost. As items arrive at the warehouse and final weights are confirmed, update your spreadsheet for a clear before-and-after picture of your shipping expenses.',
              },
              {
                q: 'Is BaseTao legit and safe to use?',
                a: 'Based on publicly available information and user reports, BaseTao operates as a shopping agent service. We encourage users to conduct their own research and review current terms before making decisions. Our website provides objective analysis to support informed decision-making.',
              },
              {
                q: 'Where can I find BaseTao coupon codes?',
                a: 'We maintain a regularly updated BaseTao Coupon page tracking publicly available discount codes and promotions. We do not generate or guarantee coupon codes, only report on publicly available information.',
              },
            ].map((faq, i) => (
              <details key={i} className="group rounded-xl border border-background-200/70 bg-background-50 overflow-hidden">
                <summary className="px-6 py-4 cursor-pointer flex items-center justify-between gap-4 text-foreground-900 font-medium text-sm">
                  {faq.q}
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center group-open:rotate-180 transition-transform duration-200">
                    <i className="ri-arrow-down-s-line text-foreground-400"></i>
                  </div>
                </summary>
                <div className="px-6 pb-5 text-sm text-foreground-500 leading-relaxed">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}